// Space Meteor Survival Client (Spectator Mode & Live Survival Rankings)
import { sounds } from '../sound.js';

export class SpaceSurvivalClient {
  constructor(socket, containerEl) {
    this.socket = socket;
    this.container = containerEl;
    this.ships = {};
    this.meteors = [];
    this.input = { up: false, down: false, left: false, right: false };
    this.isSpectating = false;

    this.render();
    this.setupSocketEvents();
    this.setupControls();
  }

  render() {
    this.container.innerHTML = `
      <div class="space-wrapper">
        <div class="space-header">
          <div class="space-meta">
            <h2>🚀 SPACE METEOR SURVIVAL</h2>
            <div id="space-spectate-tag" class="spectate-badge modal-hidden">👁️ SPECTATING MATCH</div>
          </div>
          <div id="space-timer" class="space-badge">SURVIVAL: 0s</div>
        </div>

        <div class="space-main-area">
          <div class="space-canvas-box">
            <canvas id="space-canvas" width="900" height="600"></canvas>
          </div>

          <!-- Live Survival Leaderboard -->
          <div class="space-leaderboard" id="space-leaderboard-box">
            <h3>🏆 SURVIVAL RANKINGS</h3>
            <div class="space-ranks-list" id="space-ranks-list"></div>
          </div>
        </div>

        <!-- Virtual Touch D-Pad for Mobile Players -->
        <div class="mobile-touch-bar">
          <div class="touch-dpad-cluster">
            <button id="touch-space-left" class="btn-virtual-touch">◀</button>
            <button id="touch-space-right" class="btn-virtual-touch">▶</button>
          </div>
          <div class="touch-action-cluster">
            <button id="touch-space-up" class="btn-virtual-touch">▲</button>
            <button id="touch-space-down" class="btn-virtual-touch">▼</button>
          </div>
        </div>
      </div>
    `;

    this.canvas = document.getElementById('space-canvas');
    this.ctx = this.canvas.getContext('2d');
  }

  setupControls() {
    const handleKey = (e, isDown) => {
      if (this.isSpectating) return;
      let changed = false;
      const key = e.key ? e.key.toLowerCase() : '';
      if (e.code === 'KeyW' || key === 'w' || e.code === 'ArrowUp') { this.input.up = isDown; changed = true; }
      if (e.code === 'KeyS' || key === 's' || e.code === 'ArrowDown') { this.input.down = isDown; changed = true; }
      if (e.code === 'KeyA' || key === 'a' || e.code === 'ArrowLeft') { this.input.left = isDown; changed = true; }
      if (e.code === 'KeyD' || key === 'd' || e.code === 'ArrowRight') { this.input.right = isDown; changed = true; }

      if (changed) {
        this.socket.emit('space_input', this.input);
      }
    };

    const attachTouchBtn = (btnId, keyName) => {
      const btn = document.getElementById(btnId);
      if (!btn) return;
      const setPressed = (pressed) => {
        if (this.input[keyName] !== pressed) {
          this.input[keyName] = pressed;
          this.socket.emit('space_input', this.input);
        }
      };
      btn.addEventListener('touchstart', (e) => { e.preventDefault(); setPressed(true); }, { passive: false });
      btn.addEventListener('touchend', (e) => { e.preventDefault(); setPressed(false); }, { passive: false });
      btn.addEventListener('mousedown', () => setPressed(true));
      btn.addEventListener('mouseup', () => setPressed(false));
      btn.addEventListener('mouseleave', () => setPressed(false));
    };

    attachTouchBtn('touch-space-left', 'left');
    attachTouchBtn('touch-space-right', 'right');
    attachTouchBtn('touch-space-up', 'up');
    attachTouchBtn('touch-space-down', 'down');

    this.keyDownHandler = (e) => handleKey(e, true);
    this.keyUpHandler = (e) => handleKey(e, false);

    window.addEventListener('keydown', this.keyDownHandler);
    window.addEventListener('keyup', this.keyUpHandler);
  }

  setupSocketEvents() {
    this.socket.on('space_state', (data) => {
      this.ships = data.ships;
      this.meteors = data.meteors;

      const timer = document.getElementById('space-timer');
      if (timer) timer.innerText = `SURVIVAL: ${data.elapsed}s`;

      const ranksList = document.getElementById('space-ranks-list');
      if (ranksList && data.leaderboard) {
        ranksList.innerHTML = data.leaderboard.map((p, idx) => `
          <div class="rank-row ${p.isAlive ? 'alive' : 'dead'} ${p.id === this.socket.id ? 'me' : ''}">
            <span class="rank-num">#${idx + 1}</span>
            <span class="rank-name">${p.name}</span>
            <span class="rank-stat">${p.isAlive ? '🔥 ALIVE' : `💀 ${p.survivalSeconds}s`}</span>
          </div>
        `).join('');
      }

      this.draw();
    });

    this.socket.on('space_shipDestroyed', (data) => {
      if (data.playerId === this.socket.id) {
        this.isSpectating = true;
        sounds.playExplosion();
        document.getElementById('space-spectate-tag')?.classList.remove('modal-hidden');
      }
    });

    this.socket.on('space_gameOver', (data) => {
      sounds.playVictory();
      alert(`🏆 LAST SURVIVOR CHAMPION: ${data.winner?.name || 'Pilot'} survived the cosmic storm!`);
    });
  }

  draw() {
    if (!this.ctx) return;
    this.ctx.fillStyle = '#060913';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Stars particle background
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    for (let i = 0; i < 40; i++) {
      const x = (i * 37) % this.canvas.width;
      const y = (i * 53) % this.canvas.height;
      this.ctx.fillRect(x, y, 2, 2);
    }

    // Meteors
    this.meteors.forEach((m) => {
      this.ctx.fillStyle = '#64748b';
      this.ctx.shadowColor = '#ef4444';
      this.ctx.shadowBlur = 10;
      this.ctx.beginPath();
      this.ctx.arc(m.x, m.y, m.radius, 0, Math.PI * 2);
      this.ctx.fill();
    });
    this.ctx.shadowBlur = 0;

    // Ships
    for (const id in this.ships) {
      const s = this.ships[id];
      if (!s.isAlive) continue;

      this.ctx.fillStyle = s.color;
      this.ctx.shadowColor = s.color;
      this.ctx.shadowBlur = 12;

      // Draw Spaceship Triangle
      this.ctx.beginPath();
      this.ctx.moveTo(s.x, s.y - 16);
      this.ctx.lineTo(s.x + 12, s.y + 14);
      this.ctx.lineTo(s.x - 12, s.y + 14);
      this.ctx.closePath();
      this.ctx.fill();

      // Pilot Name
      this.ctx.fillStyle = '#ffffff';
      this.ctx.shadowBlur = 0;
      this.ctx.font = 'bold 11px Rajdhani, sans-serif';
      this.ctx.textAlign = 'center';
      this.ctx.fillText(s.name, s.x, s.y - 20);
    }
  }

  destroy() {
    if (this.keyDownHandler) window.removeEventListener('keydown', this.keyDownHandler);
    if (this.keyUpHandler) window.removeEventListener('keyup', this.keyUpHandler);
    this.socket.off('space_state');
    this.socket.off('space_shipDestroyed');
    this.socket.off('space_gameOver');
  }
}
